import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, HardHat, MapPin } from "lucide-react";
import { LOGO } from "@/components/layout/Layout";
import { L } from "@/constants/labels";

/**
 * Coquille commune Login / Register (Direction A).
 * Mobile : bande marine compacte (logo + repère), formulaire dessous.
 * Desktop : panneau marine pleine hauteur à gauche (logo, promesse,
 * réassurances), formulaire centré à droite.
 */
const Fact = ({ Icon, children }) => (
  <li className="flex items-start gap-3 text-sm lg:text-base text-white/80 font-body-l leading-relaxed">
    <Icon size={18} className="text-[#E8710A] shrink-0 mt-0.5" /> {children}
  </li>
);

const WhiteLogo = ({ className }) => (
  <Link to="/" aria-label={L.brand.name}>
    <img src={LOGO} alt="" className={className} style={{ filter: "brightness(0) invert(1)" }} />
  </Link>
);

export const AuthShell = ({ promise, children }) => (
  <div className="min-h-screen flex flex-col md:flex-row bg-[#FAFAF7] font-body-l">
    {/* Mobile : bande compacte */}
    <div className="md:hidden bg-[#0F2338] text-white relative grain overflow-hidden">
      <div className="relative px-5 py-4 flex items-center justify-between">
        <WhiteLogo className="h-6 object-contain" />
        <span className="text-[11px] font-mono-l uppercase tracking-[0.22em] text-[#E8710A] font-bold whitespace-nowrap">{promise}</span>
      </div>
    </div>

    {/* Desktop : panneau marine */}
    <aside className="hidden md:flex md:w-[42%] lg:w-[45%] bg-[#0F2338] text-white relative grain overflow-hidden flex-col justify-between p-10 lg:p-14 xl:p-20" data-testid="auth-side-panel">
      <WhiteLogo className="h-8 lg:h-9 object-contain self-start" />
      <div>
        <span className="text-xs font-mono-l uppercase tracking-[0.24em] text-[#E8710A] font-bold">{promise}</span>
        <p className="font-display mt-5 text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-[-0.03em] leading-[1.02] max-w-md">
          Le bon partenaire, <span className="text-[#E8710A]">chantier par chantier.</span>
        </p>
        <ul className="mt-10 space-y-4 max-w-md">
          <Fact Icon={HardHat}>Inscription et usage gratuits. Commission de 8 % uniquement sur les chantiers réalisés via la plateforme.</Fact>
          <Fact Icon={ShieldCheck}>Attestations à vérifier avant tout engagement.</Fact>
          <Fact Icon={MapPin}>Recherche par métier, secteur et disponibilité.</Fact>
        </ul>
      </div>
      <div className="flex gap-5 text-xs text-white/50">
        <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
        <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
        <Link to="/cgu" className="hover:text-white">CGU</Link>
      </div>
    </aside>

    {/* Formulaire */}
    <main className="flex-1 flex flex-col">
      <div className="flex-1 flex md:items-center">
        <div className="w-full max-w-md md:max-w-lg mx-auto px-5 md:px-12 py-8 md:py-16">
          {children}
        </div>
      </div>
      <footer className="md:hidden border-t border-[#1B3A5C]/10 py-5 flex justify-center gap-4 text-xs text-[#475569]">
        <Link to="/mentions-legales" className="hover:underline">Mentions légales</Link>
        <Link to="/confidentialite" className="hover:underline">Confidentialité</Link>
        <Link to="/cgu" className="hover:underline">CGU</Link>
      </footer>
    </main>
  </div>
);

export const AuthKicker = ({ children, color = "#1B3A5C" }) => (
  <div className="flex items-center gap-3 mb-4 md:mb-5">
    <div className="h-px w-8" style={{ backgroundColor: color === "#1B3A5C" ? "#E8710A" : color }} aria-hidden />
    <span className="text-[11px] uppercase tracking-[0.24em] font-bold font-mono-l" style={{ color }}>{children}</span>
  </div>
);

export const AuthTitle = ({ children }) => (
  <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-[#0F2338] tracking-[-0.02em] leading-[1.1]">{children}</h1>
);

export const CARD = "bg-white border border-[#1B3A5C]/10 rounded-[8px] p-5 md:p-8 space-y-5";
